import { Button, Input } from '@sjuni/ui';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { TranslatableField } from '@/components/form/TranslatableField';
import type { TranslatedValue } from '@/lib/forms/translatable';
import type { StringListKind } from '@/routes/navigation/settingEditors';

/**
 * The structured editors for docs/05-ADMIN-DASHBOARD.md §2's `/settings/:group`.
 *
 * §8's `type` column is what picks a control, and three settings are typed
 * `json`: `contact_phones`, `contact_form_recipients` and `working_hours`. They
 * were edited as raw JSON in a monospace textarea — which is to say an
 * administrator wanting to correct a phone number had to type
 *
 *     ["(+995 365) 22 19 90", "(+995 365) 22 12 19"]
 *
 * with the brackets, the quotes and the comma in the right places, and a missed
 * one silently kept the previous value. Client review, and they are right: none
 * of these is structurally interesting enough to justify that. Two are a list of
 * strings and the third is five rows of opening times.
 *
 * The JSON textarea stays for any `json` setting *without* an editor here, which
 * is the honest fallback for a shape nobody has designed a form for yet — better
 * than a form that guesses and writes something the public site cannot read.
 */

export interface StringListFieldProps {
  label: string;
  /** The stored value, which may be anything if it was hand-edited before. */
  value: unknown;
  kind: StringListKind;
  disabled: boolean;
  onChange: (value: string[]) => void;
  meta: React.ReactNode;
}

/**
 * Reads a stored value as a list of strings without ever throwing.
 *
 * These have been editable as free JSON, so the value on disk is not guaranteed
 * to be the shape it should be. Anything unusable becomes an empty list rather
 * than an exception — a settings screen that will not open is worse than one
 * that shows a field as empty and lets it be filled in.
 */
function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return typeof value === 'string' && value !== '' ? [value] : [];
  }

  return value.filter((entry): entry is string => typeof entry === 'string');
}

export function StringListField({
  label,
  value,
  kind,
  disabled,
  onChange,
  meta,
}: StringListFieldProps) {
  const { t } = useTranslation();

  const entries = asStringList(value);

  const replace = (next: string[]): void => onChange(next);

  const move = (index: number, by: number): void => {
    const next = [...entries];
    const target = index + by;

    if (target < 0 || target >= next.length) {
      return;
    }

    // Written out rather than destructured: `noUncheckedIndexedAccess` types an
    // array read as possibly undefined, and the swap is guarded by the bounds
    // check above rather than by a non-null assertion.
    const moved = next[index] ?? '';
    next[index] = next[target] ?? '';
    next[target] = moved;

    replace(next);
  };

  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="text-body-sm font-semibold text-primary">{label}</legend>

      {/*
       * Order is not decoration here. The footer and the contact page print
       * these in order, so the first phone is the one a visitor calls — which
       * is why this has reorder controls rather than just add and remove.
       */}
      <ul className="space-y-2">
        {entries.map((entry, index) => (
          // The index *is* the identity: these are plain strings with no id, and
          // keying on the value would remount the input being typed into the
          // moment it briefly matches another row.
          <li key={index} className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                /*
                 * The position only. The `<legend>` above names the setting, and
                 * repeating it per row made every row's label a substring match
                 * for the field itself — which is a real problem for anyone
                 * scanning the form by label, not just for a test selector.
                 */
                label={t('settings.list.entry', { position: index + 1 })}
                hideLabel
                type={kind === 'text' ? 'text' : kind}
                value={entry}
                disabled={disabled}
                onChange={(event) => {
                  const next = [...entries];
                  next[index] = event.target.value;
                  replace(next);
                }}
              />
            </div>

            <div className="flex shrink-0 items-center gap-1">
              {/*
               * Icon-only, so each needs its own accessible name — the icon is
               * decoration and `Button` hides it from assistive technology.
               * The name carries the position, because "remove" three times in
               * a row tells a screen-reader user which button but not which
               * phone number.
               */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                iconLeft={<ArrowUp size={16} strokeWidth={1.75} />}
                disabled={disabled || index === 0}
                aria-label={t('settings.list.moveUp', { position: index + 1 })}
                onClick={() => move(index, -1)}
              />

              <Button
                type="button"
                variant="ghost"
                size="sm"
                iconLeft={<ArrowDown size={16} strokeWidth={1.75} />}
                disabled={disabled || index === entries.length - 1}
                aria-label={t('settings.list.moveDown', { position: index + 1 })}
                onClick={() => move(index, 1)}
              />

              <Button
                type="button"
                variant="ghost"
                size="sm"
                iconLeft={<Trash2 size={16} strokeWidth={1.75} />}
                disabled={disabled}
                aria-label={t('settings.list.remove', { position: index + 1 })}
                onClick={() => replace(entries.filter((_, at) => at !== index))}
              />
            </div>
          </li>
        ))}
      </ul>

      {entries.length === 0 ? (
        <p className="text-caption text-secondary">{t('settings.list.empty')}</p>
      ) : null}

      <Button
        type="button"
        variant="secondary"
        size="sm"
        iconLeft={<Plus size={16} strokeWidth={1.75} />}
        disabled={disabled}
        onClick={() => replace([...entries, ''])}
      >
        {t('settings.list.add')}
      </Button>

      {meta}
    </fieldset>
  );
}

/** One row of `working_hours`, as the public contact page reads it. */
interface HoursRow {
  day?: string;
  label?: TranslatedValue;
  from?: string;
  to?: string;
  closed?: boolean;
  note?: TranslatedValue;
}

/**
 * The same tolerance as `asStringList`, one level deeper: a row that is not an
 * object becomes an empty row rather than breaking the screen.
 */
function asHoursRows(value: unknown): HoursRow[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((row): HoursRow => {
    if (typeof row !== 'object' || row === null) {
      return {};
    }

    const source = row as Record<string, unknown>;

    return {
      day: typeof source.day === 'string' ? source.day : undefined,
      label: (typeof source.label === 'object' && source.label !== null
        ? source.label
        : {}) as TranslatedValue,
      from: typeof source.from === 'string' ? source.from : undefined,
      to: typeof source.to === 'string' ? source.to : undefined,
      closed: source.closed === true,
      note: (typeof source.note === 'object' && source.note !== null
        ? source.note
        : {}) as TranslatedValue,
    };
  });
}

export interface WorkingHoursFieldProps {
  label: string;
  value: unknown;
  disabled: boolean;
  onChange: (value: HoursRow[]) => void;
  meta: React.ReactNode;
}

export function WorkingHoursField({
  label,
  value,
  disabled,
  onChange,
  meta,
}: WorkingHoursFieldProps) {
  const { t } = useTranslation();

  const rows = asHoursRows(value);

  const patch = (index: number, changes: Partial<HoursRow>): void => {
    onChange(rows.map((row, at) => (at === index ? { ...row, ...changes } : row)));
  };

  return (
    <fieldset className="space-y-3" disabled={disabled}>
      <legend className="text-body-sm font-semibold text-primary">{label}</legend>

      <ul className="space-y-3">
        {rows.map((row, index) => (
          <li key={index} className="rounded-card border border-subtle p-3">
            <div className="space-y-3">
              {/* The row's own name, bilingual — "ორშაბათი - პარასკევი" is one
                  row rather than five, which is a choice the label carries. */}
              <TranslatableField
                label={t('settings.hours.label')}
                name={`working_hours.${index}.label`}
                value={row.label ?? {}}
                onChange={(next) => patch(index, { label: next as TranslatedValue })}
                required={false}
                disabled={disabled}
              />

              <label className="flex items-center gap-2 text-body-sm text-primary">
                <input
                  type="checkbox"
                  checked={row.closed === true}
                  disabled={disabled}
                  onChange={(event) => patch(index, { closed: event.target.checked })}
                  className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
                />
                {t('settings.hours.closed')}
              </label>

              {row.closed === true ? (
                /*
                 * Only when closed, and only then. §11's Saturday is "closed
                 * (for events only)" and its Sunday is closed flatly, so the
                 * note is a qualification on being closed rather than a field
                 * that means anything beside opening times.
                 */
                <TranslatableField
                  label={t('settings.hours.note')}
                  name={`working_hours.${index}.note`}
                  value={row.note ?? {}}
                  onChange={(next) => patch(index, { note: next as TranslatedValue })}
                  required={false}
                  disabled={disabled}
                />
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    label={t('settings.hours.from')}
                    type="time"
                    value={row.from ?? ''}
                    disabled={disabled}
                    onChange={(event) => patch(index, { from: event.target.value })}
                  />
                  <Input
                    label={t('settings.hours.to')}
                    type="time"
                    value={row.to ?? ''}
                    disabled={disabled}
                    onChange={(event) => patch(index, { to: event.target.value })}
                  />
                </div>
              )}
            </div>

            <div className="mt-3 flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                iconLeft={<Trash2 size={16} strokeWidth={1.75} />}
                disabled={disabled}
                onClick={() => onChange(rows.filter((_, at) => at !== index))}
              >
                {t('settings.hours.removeRow')}
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {rows.length === 0 ? (
        <p className="text-caption text-secondary">{t('settings.hours.empty')}</p>
      ) : null}

      <Button
        type="button"
        variant="secondary"
        size="sm"
        iconLeft={<Plus size={16} strokeWidth={1.75} />}
        disabled={disabled}
        onClick={() =>
          onChange([...rows, { label: {}, from: '', to: '', closed: false, note: {} }])
        }
      >
        {t('settings.hours.addRow')}
      </Button>

      {meta}
    </fieldset>
  );
}
